import { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'transmute/debounce';
import { DataFetchingClient } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Small } from 'foundations-components/transitional/components/Small';
import { H4 } from 'foundations-components/transitional/components/H4';
import AddGuestsField from '../input/AddGuestsField';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { getAllGuestEmails } from '../../selectors/AddGuestsFormSelectors';
import { addGuestEmail as addGuestEmailAction, removeGuestEmail as removeGuestEmailAction } from '../../redux/actions/FormActions';
import { getCanAddGuests, getMaxGuestCount } from 'MeetingsLib/selectors/BookInfoSelectors';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { View } from 'foundations-components/components/View';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const client = new DataFetchingClient();
const AddGuestsForm = ({
  isRescheduling
}) => {
  const dispatch = useDispatch();
  const guestEmails = useSelector(getAllGuestEmails);
  const canAddGuests = useSelector(getCanAddGuests);
  const maxGuestCount = useSelector(getMaxGuestCount);
  const [guestEmail, setGuestEmail] = useState('');
  const [emailToValidate, setEmailToValidate] = useState('');
  const [emailIsChanging, setEmailIsChanging] = useState(false);
  const {
    isValid: emailIsValid,
    loading: isValidatingEmail
  } = useEmailValidation({
    email: emailToValidate,
    shouldSkip: !canAddGuests,
    client
  });
  const handleInputChange = useCallback(evt => {
    const email = evt.target.value;
    setGuestEmail(email.trim().toLowerCase());
    setEmailIsChanging(true);
  }, []);
  const updateEmailToValidate = useCallback(email => {
    setEmailToValidate(email);
    setEmailIsChanging(false);
  }, []);
  const debouncedEmailToValidateChange = useMemo(() => debounce(500, nextVal => updateEmailToValidate(nextVal)), [updateEmailToValidate]);
  const handleAddGuest = useCallback(() => {
    dispatch(addGuestEmailAction(guestEmail));
    setGuestEmail('');
    setEmailToValidate('');
  }, [dispatch, guestEmail]);
  const handleRemoveGuest = useCallback(guestToDelete => {
    dispatch(removeGuestEmailAction(guestToDelete));
  }, [dispatch]);
  useEffect(() => {
    debouncedEmailToValidateChange(guestEmail);
  }, [debouncedEmailToValidateChange, guestEmail]);
  const formHeader = useMemo(() => {
    return /*#__PURE__*/_jsxs(View, {
      paddingInline: "200",
      paddingBlockStart: "400",
      as: "div",
      children: [/*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        gap: 5,
        paddingBlockEnd: "400",
        children: [/*#__PURE__*/_jsx(H4, {
          tabIndex: 0,
          margin: "0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "prebook.details.addGuests.label"
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          "data-test-id": "meetings-add-guests-header-tooltip",
          title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "prebook.details.addGuests.privacyPolicyTooltip_jsx",
            options: {
              href: 'https://legal.hubspot.com/privacy-policy',
              external: true
            },
            elements: {
              Link
            }
          }),
          maxInlineSize: "250px",
          children: /*#__PURE__*/_jsx(InfoIcon, {
            size: "300",
            color: "icon-core-default"
          })
        })]
      }), /*#__PURE__*/_jsx(Paragraph, {
        typography: "body-200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.details.addGuests.description",
          options: {
            maxCount: maxGuestCount
          }
        })
      })]
    });
  }, [maxGuestCount]);
  const guestList = useMemo(() => {
    if (guestEmails.size === 0) {
      return /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.details.addGuests.guestList"
        })
      });
    }
    return /*#__PURE__*/_jsx(List, {
      inline: true,
      children: guestEmails.map(guest => {
        return /*#__PURE__*/_jsx(ListItem, {
          paddingInlineStart: "0",
          paddingInlineEnd: "200",
          children: /*#__PURE__*/_jsx(Tag, {
            "data-selenium-test": "meetings-add-guests-form-tag",
            "data-test-id": "meetings-add-guests-form-tag",
            closeable: true,
            onCloseClick: () => {
              handleRemoveGuest(guest);
            },
            children: guest
          })
        }, guest);
      })
    });
  }, [guestEmails, handleRemoveGuest]);
  const emailToValidateIsChanging = isValidatingEmail || emailIsChanging;
  const emailToValidateIsChangingOrEmpty = emailToValidateIsChanging || !emailToValidate;
  const isValid = emailToValidateIsChangingOrEmpty || emailIsValid;
  const emailAlreadyExists = !emailToValidateIsChanging && guestEmails.has(guestEmail);
  const renderedErrorMessage = useMemo(() => {
    if (!isValid) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.details.errorHelper.email"
      });
    }
    if (emailAlreadyExists) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.details.addGuests.emailExists"
      });
    }
    return null;
  }, [isValid, emailAlreadyExists]);
  const renderedTooltipMessage = useMemo(() => {
    if (guestEmails.size === maxGuestCount) return null;
    let tooltipMessage = '';
    if (guestEmail.length === 0) {
      tooltipMessage = 'prebook.details.addGuests.emailRequiredTooltip';
    } else if (!isValid) {
      tooltipMessage = 'prebook.details.errorHelper.email';
    } else if (emailAlreadyExists) {
      tooltipMessage = 'prebook.details.addGuests.emailExists';
    }
    return tooltipMessage ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: tooltipMessage
    }) : null;
  }, [isValid, emailAlreadyExists, guestEmail, guestEmails, maxGuestCount]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(View, {
      "data-selenium-test": "add-guests-form",
      "data-test-id": "add-guests-form",
      id: "add-guests-form",
      paddingInline: "600",
      paddingBlock: "400",
      as: "div",
      children: [formHeader, /*#__PURE__*/_jsx(AddGuestsField, {
        guestEmail: guestEmail,
        guestCount: guestEmails.size,
        errorMessage: renderedErrorMessage,
        tooltipMessage: renderedTooltipMessage,
        isValidatingEmail: emailToValidateIsChanging,
        maxGuestCount: maxGuestCount,
        handleAddGuest: handleAddGuest,
        handleInputChange: handleInputChange,
        isRescheduling: isRescheduling
      }), /*#__PURE__*/_jsx(TFlex, {
        paddingInline: "200",
        paddingBlockStart: "400",
        children: guestList
      })]
    })
  });
};
export default AddGuestsForm;